import useSWR from 'swr';
import type { BundleMetadata, GroupType } from '@/components/bundle/bundleTypes';
import { backendClient } from '@/lib/api-client';

type BundleCreateMetadata = Pick<BundleMetadata, 'remove_blank_pages'> & {
  page_numbers: Pick<BundleMetadata['page_numbers'], 'enabled'>;
  bates_stamp: Pick<BundleMetadata['bates_stamp'], 'enabled'>;
};

export interface BundleFileResponse {
  bundle_file_id: string;
  file_reference_guid: string;
  order_index: number;
  custom_name: string | null;
  page_range: string | null;
  file_name: string;
  source_file_name: string;
}

export interface EmailAttachmentInfoResponse {
  file_reference_guid: string;
  file_name: string;
  source_file_name: string;
}

export interface BundleGroupResponse {
  bundle_group_id: string;
  title: string;
  order_index: number;
  group_type: GroupType;
  files: BundleFileResponse[];
  subgroups: BundleGroupResponse[];
}

export interface BundleResponse {
  bundle_id: string;
  matter_id: number;
  title: string;
  description: string | null;
  metadata: BundleMetadata;
  created_at: string;
  created_by: number;
  updated_at: string | null;
  updated_by: number | null;
  groups: BundleGroupResponse[];
  /** Attachments discovered for each email group, keyed by bundle_group_id. */
  email_attachments: Record<string, EmailAttachmentInfoResponse[]>;
}

export interface BundleGroupCreate {
  bundle_group_id: string | null;
  title: string;
  order_index: number;
  group_type: GroupType;
  files: BundleFileCreate[];
  subgroups: BundleGroupCreate[];
}

export interface BundleFileCreate {
  bundle_file_id: string | null;
  file_reference_guid: string;
  order_index: number;
  custom_name?: string | null;
  page_range?: string | null;
}

export interface BundleCreateRequest {
  title: string;
  description?: string | null;
  metadata?: BundleCreateMetadata;
  groups: BundleGroupCreate[];
}

export interface BundleUpdateRequest {
  title?: string;
  description?: string | null;
  metadata: BundleMetadata;
  groups: BundleGroupCreate[];
}

const fetcher = async (url: string) => {
  const response = await backendClient.get(url);
  return response.data;
};

export const useBundle = (matterGuid: string | undefined, bundleId: string | undefined) => {
  const { data, error, isLoading, mutate } = useSWR<BundleResponse>(
    matterGuid && bundleId ? `/bundle/matter/${matterGuid}/${bundleId}` : null,
    fetcher
  );

  const updateBundle = async (update: BundleUpdateRequest) => {
    const response = await backendClient.put(`/bundle/matter/${matterGuid}/${bundleId}`, update);
    // PUT returns via the same `_bundle_to_response` helper as GET, so the response is the
    // authoritative new state. Skip SWR's default revalidation to avoid a wasted GET.
    mutate(response.data, { revalidate: false });
    return response.data;
  };

  return {
    bundle: data ?? null,
    isLoading,
    isError: error,
    mutate,
    updateBundle,
  };
};

export const createBundle = async (matterGuid: string, data: BundleCreateRequest) => {
  const response = await backendClient.post(`/bundle/matter/${matterGuid}`, data);
  return response.data as BundleResponse;
};
